import { COLUMN_SELECT_ID } from "@/constants";
import { useCallback, useRef } from "react";
import { CopySelectionStore } from "../context/CopySelectionContext";
import { CellAddress } from "../utils/cellAddress";

interface UseSelectionRangeParams {
  visibleColumnOrder: string[];
  store: CopySelectionStore;
}

export default function useSelectionRange({
  visibleColumnOrder,
  store,
}: UseSelectionRangeParams) {
  const startRef = useRef<CellAddress | null>(null);
  const endRef = useRef<CellAddress | null>(null);
  const isSelectingRef = useRef(false);

  const computeSelectionRange = useCallback(
    (start: CellAddress, end: CellAddress) => {
      if (start.rowIndex === end.rowIndex && start.columnId === end.columnId) {
        return null;
      }

      const copyableColumns = visibleColumnOrder.filter(
        (id) => id !== COLUMN_SELECT_ID,
      );
      const startColIndex = copyableColumns.indexOf(start.columnId);
      const endColIndex = copyableColumns.indexOf(end.columnId);
      if (startColIndex === -1 || endColIndex === -1) return null;

      const minCol = Math.min(startColIndex, endColIndex);
      const maxCol = Math.max(startColIndex, endColIndex);

      return {
        minRow: Math.min(start.rowIndex, end.rowIndex),
        maxRow: Math.max(start.rowIndex, end.rowIndex),
        selectedColumnIds: new Set(copyableColumns.slice(minCol, maxCol + 1)),
      };
    },
    [visibleColumnOrder],
  );

  const setStart = useCallback((address: CellAddress | null) => {
    startRef.current = address;
  }, []);

  const setEnd = useCallback((address: CellAddress | null) => {
    endRef.current = address;
  }, []);

  const getStart = useCallback(() => startRef.current, []);
  const getEnd = useCallback(() => endRef.current, []);

  const setSelecting = useCallback((selecting: boolean) => {
    isSelectingRef.current = selecting;
  }, []);

  const isSelecting = useCallback(() => isSelectingRef.current, []);

  const updateRange = useCallback(
    (start: CellAddress, end: CellAddress) => {
      const range = computeSelectionRange(start, end);
      store.setRange(range);
      if (range) {
        window.getSelection()?.removeAllRanges();
      }
    },
    [computeSelectionRange, store],
  );

  const clearSelection = useCallback(() => {
    startRef.current = null;
    endRef.current = null;
    isSelectingRef.current = false;
    store.clear();
  }, [store]);

  const getSelectionBounds = useCallback(() => {
    return { start: startRef.current, end: endRef.current };
  }, []);

  return {
    setStart,
    setEnd,
    getStart,
    getEnd,
    setSelecting,
    isSelecting,
    updateRange,
    clearSelection,
    getSelectionBounds,
  };
}
